import React, { useEffect, useMemo, useState } from 'react';
import { AdminShell } from '../../components/AdminShell';
import { SmsHygieneMeter } from '../../components/SmsHygieneMeter';
import { supabase } from '../../lib/supabase';
import { Mail, MessageSquare, Save, MessageCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Kept in sync with `/api/templates/*` sample payloads used by the
// shop-admin editor — same tokens, same preview grammar. Master admin
// only edits platform DEFAULTS (shop_id IS NULL rows); shop overrides
// live on the shop-admin surface.
const SAMPLES = {
  customer_name: 'Alex Rivera',
  customer_first_name: 'Alex',
  shop_name: 'Sharp & Sons',
  shop_sms_name: 'Sharp & Sons',
  barber_name: 'Marcus Bell',
  barber_first_name: 'Marcus',
  service_name: 'Cut & Beard Trim',
  starts_at_date: 'Wed 23 Jul',
  starts_at_time: '10:30',
  cancel_url: 'https://instacut.co.uk/cancel/abc123',
  short_cancel_url: 'https://instacut.co.uk/c/aB3d9F',
  diary_url: 'https://instacut.co.uk/diary',
  customer_phone: '07700 900123',
};
const renderPreview = (s) =>
  String(s || '').replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (_, k) => SAMPLES[k] ?? `{{${k}}}`);

const PlatformTemplatesInner = () => {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const [channelFilter, setChannelFilter] = useState('all'); // all | email | sms

  const filteredRows = useMemo(
    () => rows.filter(r => {
      if (channelFilter === 'all')    return true;
      if (channelFilter === 'system') return r.plan === 'system';
      // 'email' / 'sms' also exclude system rows so they don't show up
      // in the customer-facing channel views.
      return r.channel === channelFilter && r.plan !== 'system';
    }),
    [rows, channelFilter],
  );

  const authHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };
  };

  const load = async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${BACKEND_URL}/api/templates/admin/platform-defaults`, { headers });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'load failed');
      setRows(j.templates || []);
      if (!selected && j.templates?.length) {
        setSelected(`${j.templates[0].kind}|${j.templates[0].channel}`);
      }
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const current = useMemo(() => {
    if (!selected) return null;
    const [k, c] = selected.split('|');
    return rows.find(r => r.kind === k && r.channel === c) || null;
  }, [rows, selected]);

  useEffect(() => {
    if (current) setDraft({
      subject: current.subject || '',
      body: current.body || '',
      enabled: current.enabled,
      allow_unicode: false,
    });
  }, [current]);

  const dirty = draft && current && (
    (draft.subject || '') !== (current.subject || '') ||
    (draft.body    || '') !== (current.body    || '') ||
    draft.enabled  !== current.enabled
  );

  const save = async () => {
    if (!current || !draft) return;
    setSaving(true); setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${BACKEND_URL}/api/templates/admin/platform-defaults`, {
        method: 'POST', headers,
        body: JSON.stringify({
          kind: current.kind, channel: current.channel,
          subject: current.has_subject ? draft.subject : null,
          body: draft.body,
          enabled: draft.enabled,
          allow_unicode: !!draft.allow_unicode,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'save failed');
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      await load();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageCircle size={18} /> Message templates (platform defaults)
          </h2>
          <div className="sub">
            Copy every new shop inherits, and every existing shop that hasn't overridden the
            template falls back to. SMS templates are validated as GSM-7 to keep costs down.
          </div>
        </div>
      </div>

      <div className="body">
        {loading ? null : (
          <div className="templates-layout">
            {/* Sidebar */}
            <div className="templates-list" data-testid="platform-templates-list">
              {/* Channel filter tabs */}
              <div style={{ display: 'flex', gap: 4, padding: '8px 8px 4px' }} data-testid="platform-templates-filter">
                {['all', 'email', 'sms', 'system'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setChannelFilter(t)}
                    data-testid={`platform-templates-filter-${t}`}
                    style={{
                      flex: 1,
                      padding: '4px 6px',
                      fontSize: 11,
                      fontWeight: 600,
                      border: 0,
                      borderRadius: 6,
                      cursor: 'pointer',
                      background: channelFilter === t ? 'var(--acc, #6366f1)' : 'transparent',
                      color: channelFilter === t ? '#fff' : 'var(--text-mute)',
                      textTransform: 'uppercase',
                    }}
                  >{t}</button>
                ))}
              </div>
              {filteredRows.map(r => {
                const key = `${r.kind}|${r.channel}`;
                const active = key === selected;
                const Icon = r.channel === 'email' ? Mail : MessageSquare;
                return (
                  <button
                    key={key}
                    className={`templates-item${active ? ' is-active' : ''}`}
                    onClick={() => setSelected(key)}
                    data-testid={`platform-tpl-item-${r.kind}-${r.channel}`}
                  >
                    <Icon size={14} style={{ flexShrink: 0, marginTop: 2, color: r.channel === 'email' ? 'var(--acc)' : '#059669' }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="ti-title" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {r.kind.replace(/_/g, ' ')}
                        <span className="ti-channel">{r.channel.toUpperCase()}</span>
                        <PlanPill plan={r.plan} />
                      </div>
                      <div className="ti-desc">{r.description}</div>
                    </div>
                    {r.channel === 'sms' && r.sms_analysis && !r.sms_analysis.is_gsm7 && (
                      <span
                        title="This SMS body contains non-GSM-7 characters and will send as UCS-2"
                        style={{ marginLeft: 'auto', color: '#dc2626', fontSize: 10, fontWeight: 700 }}
                      >UCS-2</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Editor */}
            {current && draft && (
              <div className="templates-editor" data-testid="platform-templates-editor">
                <div className="templates-meta">
                  <div>
                    <div className="tm-kind">{current.kind.replace(/_/g, ' ')}</div>
                    <div className="tm-desc">{current.description}</div>
                  </div>
                  {savedFlash && <span className="pill green" style={{ marginLeft: 'auto' }}>Saved</span>}
                </div>

                {error && <div className="error-message" data-testid="platform-tpl-error">{error}</div>}

                {current.has_subject && (
                  <div className="field">
                    <label>Subject</label>
                    <input
                      className="inp"
                      value={draft.subject}
                      onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                      disabled={saving}
                      data-testid="platform-tpl-subject"
                    />
                  </div>
                )}

                <div className="field">
                  <label>{current.channel === 'email' ? 'Body (markdown)' : 'SMS body'}</label>
                  <textarea
                    className="inp"
                    rows={current.channel === 'email' ? 10 : 4}
                    value={draft.body}
                    onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                    disabled={saving}
                    data-testid="platform-tpl-body"
                  />
                  <div className="tm-tokens">
                    Available tokens:
                    {current.tokens.map(t => (
                      <button
                        key={t}
                        type="button"
                        className="token-chip"
                        disabled={saving}
                        onClick={() => setDraft(d => ({ ...d, body: (d.body || '') + `{{${t}}}` }))}
                        data-testid={`platform-tpl-token-${t}`}
                      >{`{{${t}}}`}</button>
                    ))}
                  </div>
                  {current.channel === 'sms' && (
                    <SmsHygieneMeter
                      body={draft.body}
                      onNormalisedChange={(clean) => setDraft(d => ({ ...d, body: clean }))}
                      allowUnicode={!!draft.allow_unicode}
                      onAllowUnicodeChange={(v) => setDraft(d => ({ ...d, allow_unicode: v }))}
                    />
                  )}
                </div>

                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--mute)', marginTop: 6 }}
                  data-testid="platform-tpl-enabled-row"
                >
                  <input
                    type="checkbox"
                    checked={!!draft.enabled}
                    disabled={saving}
                    onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                    data-testid="platform-tpl-enabled"
                  />
                  Enabled by default for every shop (a shop can still silence it locally)
                </label>

                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button
                    className="btn p"
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid="platform-tpl-save"
                  >
                    <Save size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                    {saving ? 'Saving…' : 'Save platform default'}
                  </button>
                  {dirty && (
                    <button
                      className="btn s"
                      onClick={() => setDraft({
                        subject: current.subject || '',
                        body: current.body || '',
                        enabled: current.enabled,
                        allow_unicode: false,
                      })}
                      disabled={saving}
                      data-testid="platform-tpl-discard"
                    >Discard</button>
                  )}
                </div>

                <div className="templates-preview" data-testid="platform-tpl-preview">
                  <div className="tp-head">Preview with sample data</div>
                  {current.has_subject && (
                    <div className="tp-subject">{renderPreview(draft.subject)}</div>
                  )}
                  <div className="tp-body">
                    {renderPreview(draft.body).split('\n').map((line, i) => (
                      <p key={i}>{line || <br />}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

const PlanPill = ({ plan }) => {
  const meta = plan === 'premium'
    ? { bg: '#7c3aed', label: 'PREMIUM', title: 'Premium plans only' }
    : plan === 'system'
      ? { bg: '#0891b2', label: 'SYSTEM',   title: 'Master admin only — fired by backend triggers' }
      : { bg: '#6b7280', label: 'ALL PLANS', title: 'All plans' };
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 700,
        padding: '1px 6px',
        borderRadius: 4,
        letterSpacing: 0.4,
        background: meta.bg,
        color: '#fff',
      }}
      title={meta.title}
    >
      {meta.label}
    </span>
  );
};

const PlatformTemplates = () => (
  <AdminShell>
    <PlatformTemplatesInner />
  </AdminShell>
);
export default PlatformTemplates;
